import { clearIndexedDbPersistence } from 'firebase/firestore';
import { db } from '../firebase';

/**
 * Claves de localStorage/sessionStorage que contienen datos de la aplicación.
 * Se purgan por prefijo para que no queden restos al añadir nuevas cachés.
 */
const STORAGE_PREFIXES = [
  // Datos personales de alumnos (los más sensibles)
  'directorio_maestro',
  'student_cache_',
  'instclass_students_',
  'instclass_metadata_',
  'inst_class_cache_',
  'personal_class_students_',
  'personal_class_refs_',
  'pending_attendances_',
  'vps_outbox_',
  'attendance_history_',
  // Configuración y cachés de clases
  'institution_data',
  'classes_from_index',
  'clases_institucionales_',
  'global_inst_ver',
  'personal_classes_',
  'resumen_institucional_cache',
  'class_notes_cache_',
  'calendar_events_',
  'avisos_institucionales',
  'timetable_',
  'user_name_',
  'favorites_',
  'canCopy_',
  // Identidad
  'user_doc_v',
  'sesion_ultima',
  'userTheme',
  // Estado de sesión y PWA
  'page-has-been-force-refreshed',
  'pwa_update_prompted',
  'pwa_updating',
  'sync_banner_msg'
];

function purgeStorage(storage) {
  if (!storage) return 0;
  let removed = 0;
  const keys = [];
  for (let i = 0; i < storage.length; i++) {
    const key = storage.key(i);
    if (key) keys.push(key);
  }
  keys.forEach(key => {
    if (STORAGE_PREFIXES.some(p => key.startsWith(p))) {
      storage.removeItem(key);
      removed++;
    }
  });
  return removed;
}

/**
 * Elimina los datos de la sesión del dispositivo.
 *
 * Se invoca al cerrar sesión. Es importante en equipos compartidos (sala de
 * profesores): sin esto, el padrón completo de alumnos, sus asistencias y sus
 * anécdotas permanecen legibles en el navegador sin necesidad de credenciales.
 *
 * @param {Object} options
 * @param {boolean} options.includeDb - purgar también la caché persistente de
 *   Firestore (IndexedDB). Por defecto sí.
 * @returns {Promise<{localKeys: number, sessionKeys: number, dbCleared: boolean}>}
 */
export async function clearSessionData({ includeDb = true } = {}) {
  let localKeys = 0;
  let sessionKeys = 0;

  try {
    localKeys = purgeStorage(window.localStorage);
  } catch (err) {
    console.warn('[Session] No se pudo limpiar localStorage:', err);
  }

  try {
    sessionKeys = purgeStorage(window.sessionStorage);
  } catch (err) {
    console.warn('[Session] No se pudo limpiar sessionStorage:', err);
  }

  let dbCleared = false;
  if (includeDb) {
    try {
      // Firestore guarda en IndexedDB todos los documentos leídos (nóminas,
      // asistencia, anécdotas). Debe purgarse o quedan en disco indefinidamente.
      await clearIndexedDbPersistence(db);
      dbCleared = true;
    } catch (err) {
      // Falla si hay listeners activos. Se reintenta en el siguiente arranque
      // mediante el guardián de versión; no se propaga para no romper el logout.
      console.warn('[Session] No se pudo purgar la caché de Firestore:', err);
    }
  }

  return { localKeys, sessionKeys, dbCleared };
}
